<template>
  <section class="section">
    <div class="container">
      <div class="columns">
        <div class="column">
          <div class="columns is-desktop">
            <div class="column is-2">
              <div class="field">
                <div class="control">
                  <div class="select is-fullwidth">
                    <select v-model="perPage" @change="onPerPageChange">
                      <option value="10">10 per page</option>
                      <option value="15">15 per page</option>
                      <option value="25">25 per page</option>
                      <option value="50">50 per page</option>
                      <option value="100">100 per page</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div class="column is-3">
              <div class="field">
                <div class="control">
                  <input type="text" class="input" placeholder="Search" v-model="searchText" @input="changeSearchText">
                </div>
              </div>
            </div>
            <div class="column is-3">
              <date-picker v-model="dates" range></date-picker>
            </div>
            <div class="column is-2">
              <button class="button is-info is-fullwidth" @click="onFilter">Search</button>
            </div>
            <div class="column is-2">
              <button @click="exportAll" class="mb-2 button is-link is-fullwidth">Export All</button>
              <button v-if="$store.getters.isSpecialAdminUser" @click="exportMonthlyMetrics" class="button is-link is-fullwidth is-warning">Export Monthly Metrics</button>
            </div>
          </div>

          <b-table :data="records" :loading="isLoading" paginated backend-pagination :total="totalRows"
            :per-page="perPage" :current-page="currentPage" @page-change="onPageChange" backend-sorting
            :default-sort-direction="defaultSortOrder" :default-sort="[sortField, sortOrder]" @sort="onSort">
            <template slot-scope="props">
              <b-table-column field="userId" label="User ID" sortable>{{ props.row.userId }}</b-table-column>
              <b-table-column field="userEmail" label="Email" sortable>{{ props.row.userEmail }}</b-table-column>
              <b-table-column field="id" label="Order ID" sortable numeric>{{ props.row.id }}</b-table-column>
              <b-table-column field="order_paid_at" label="Order Paid Date" sortable>{{ props.row.order_paid_at }}</b-table-column>
              <b-table-column field="page_run_id" label="Page Run ID">#{{ props.row.page_run_id }}</b-table-column>
              <b-table-column field="page_run_score" label="Original Page Run Score">{{ props.row?.pageRunResult?.pageRunScore }}</b-table-column>
              <b-table-column field="keyword" label="Keyword">{{ props.row.keyword }}</b-table-column>
              <b-table-column field="fulfillement_status" label="Status" sortable>
                <b-dropdown
                  aria-role="list" @click.native.stop
                >
                  <template #trigger>
                    <v-btn
                      small
                    >
                      {{props.row.fulfillement_status}}
                    </v-btn>
                  </template>
                  <b-dropdown-item aria-role="listitem" :disabled="props.row.fulfillement_status === 'pending'" @click.native.stop="updateOrder(props.row.id, {'set_pending': true}, true)">Pending</b-dropdown-item>
                  <b-dropdown-item aria-role="listitem" :disabled="props.row.fulfillement_status === 'in_progress'" @click.native.stop="updateOrder(props.row.id, {'set_in_progress': true}, true)">In Progress</b-dropdown-item>
                  <b-dropdown-item aria-role="listitem" :disabled="props.row.fulfillement_status === 'completed'" @click.native.stop="updateOrder(props.row.id, {'set_completed': true}, true)">Completed</b-dropdown-item>
                </b-dropdown>
              </b-table-column>
              <b-table-column field="credits_used" label="Credits used" sortable numeric>
                {{ props.row.credits_used }}
              </b-table-column>
              <b-table-column field="actions" label="Actions">
                <div class="is-flex is-align-items-center">
                  <v-tooltip
                    v-if="!Boolean(POPReportTaskIntervals[props.row.id]) && props.row.guard_run_status === 'completed'"
                    top color="black"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <img
                        src="/static/eye.svg"
                        class="mr-3"
                        style="cursor: pointer; width: 24px; height: 24px;"
                        @click="getPDFReport(props.row.id, 'top_level')"
                        alt="View"
                        v-bind="attrs" v-on="on"
                      />
                    </template>
                    <div>
                      View report
                    </div>
                  </v-tooltip>

                  <v-tooltip
                    v-if="!Boolean(POPReportTaskIntervals[props.row.id]) && props.row.guard_run_status === 'completed'"
                    top color="black"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <img
                        src="/static/edit.svg"
                        class="mr-3"
                        style="cursor: pointer; width: 24px; height: 24px;"
                        @click="$router.push({ name: 'AdminOptimizationDetail', params: {
                          reviewOptimizationOrderId: props.row.id,
                          pageRunId: props.row.page_run_id,
                          keyword: props.row.keyword,
                        } })"
                        alt="Edit"
                        v-bind="attrs" v-on="on"
                      />
                    </template>
                    <div>
                      Edit report
                    </div>
                  </v-tooltip>

                  <template v-if="props.row.fulfillement_status !== 'completed'">
                    <v-tooltip
                      v-if="!(POPReportTaskIntervals[props.row.id]?.status?.status === 'PROGRESS')"
                      top color="black"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <i
                          class="material-icons-outlined mr-3"
                          style="cursor: pointer; width: 24px; height: 24px; color: var(--v-grayColor-base);"
                          @click="updateOrder(props.row.id, {'refresh_guard_run': true}, true)"
                          v-bind="attrs" v-on="on"
                        >refresh</i>
                      </template>
                      <div>
                        Refresh report data
                      </div>
                    </v-tooltip>
                    <b-icon
                      v-else
                      pack="fa"
                      icon="spinner"
                      custom-class="fa-spin">
                    </b-icon>
                  </template>

                  <template v-if="props.row.fulfillement_status === 'completed'">
                    <a v-if="!props.row.completed_email_sent_at" href="#" @click.prevent="updateOrder(props.row.id, {'send_completed_email': true}, true)" style="color: #0075FF;">Send email</a>
                    <v-tooltip
                      v-else
                      top color="black"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on"><i class="material-icons-outlined" style="font-size: 0.85rem; color: green;">check</i> Email sent</span>
                      </template>
                      <div>
                        Sent at {{ props.row.completed_email_sent_at }}
                      </div>
                    </v-tooltip>
                  </template>
                </div>
              </b-table-column>
            </template>
          </b-table>
        </div>
      </div>

    </div>
  </section>
</template>

<script>
import { mixin } from '@/components/mixin'

import { mixin as exportmixin } from '@/components/export_mixin'
import { toLocalDateString } from '@/utils'
import moment from 'moment'
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import {
  downloadFile,
  downloadWordFile,
  downloadEditorReport,
  generateSchemasJSON,
  getReportHeaderImage,
} from "@/utils/download-reports.js"

export default {
  components: { DatePicker },
  mixins: [mixin, exportmixin],
  data () {
    return {
      records: [],
      dates: [moment().subtract(1, 'months').toDate(), moment().toDate()],
      totalRows: 0,
      isLoading: false,
      sortField: 'createdAt',
      sortOrder: 'desc',
      defaultSortOrder: 'desc',
      currentPage: 1,
      perPage: 15,
      searchText: '',
      POPReportTaskIntervals: {},
    }
  },
  watch: {
    records: {
      immediate: true,  // means we refresh on mount as well
      handler:function () {
        this.refreshPOPReportTaskMonitors()
      }
    },
  },
  methods: {
    exportRecords(records) {
      const rows = records.map((record) =>
        [
          record.userId,
          record.userEmail,
          record.id,
          record.order_paid_at ? moment.utc(record.order_paid_at).format() : '',
          record.page_run_id,
          record?.pageRunResult?.pageRunScore,
          record.keyword,
          record.fulfillement_status,
          record.credits_used,
        ].join(',')
      );
      const data = ['User ID,User Email,Order ID,Order Paid Date,Page Run ID,Original Page Run Score,Keyword,Status,Credits used']
        .concat(rows)
        .join('\n');
      const now = new Date();
      const fileName = "ReviewOptimizationOrders_" + now.getUTCFullYear() + "_" + (now.getUTCMonth() + 1) + "_" + now.getUTCDate() + ".csv"
      this.exportToCSV(data, fileName)
    },
    exportPage() {
      this.exportRecords(this.records)
    },
    exportAll() {
      this.$store.commit('showLoading')
      const params = {
        order_status: 'paid',
        include_ai_and_page_run_result: 1,
        paginate: 0,
        sortField: this.sortField,
        sortOrder: this.sortOrder,
        filterText: this.searchText
      }
      this.$store.dispatch('getReviewOptimizationOrders', params )
        .then(response => {
          this.exportRecords(response.data.data)
        })
        .finally(() => {
          this.$store.commit('hideLoading')
        })
    },
    toLocalDateString(dateStr) {
      return toLocalDateString(dateStr)
    },
    onPerPageChange() {
      this.$nextTick(() => this.loadData())
    },
    onPageChange(page) {
      this.currentPage = page
      this.loadData()
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order
      this.loadData()
    },
    onFilter() {
      // console.log('filtering')
      this.$nextTick(() => {
        this.loadData()
      })
    },
    loadData(silent=false) {
      if (!silent) {
        this.isLoading = true
        this.$store.commit('showLoading')
      }

      let startDate = this.dates[0]?moment(this.dates[0]).startOf('day').toDate():null;
      let endDate = this.dates[1]?moment(this.dates[1]).endOf('day').toDate():null;

      let params = {
        order_status: 'paid',
        include_ai_and_page_run_result: 1,
        paginate: 1,
        sortField: this.sortField,
        sortOrder: this.sortOrder,
        page: this.currentPage,
        perPage: this.perPage,
        filterText: this.searchText
      }

      if (startDate) {
        params['startDate'] = startDate.getTime()
      }
      if (endDate) {
        params['endDate'] = endDate.getTime()
      }

      this.$store.dispatch('getReviewOptimizationOrders', params)
        .then(({ data }) => {
          const { total, records, totalPaid } = data
          this.totalRows = total
          this.records = records
          this.totalPaid = totalPaid
        })
        .finally(() => {
          if (!silent) {
            this.isLoading = false
            this.$store.commit('hideLoading')
          }
        })
    },
    changeSearchText() {
      console.log(this.searchText);
    },
    updateOrder(order_id, payload, silent = false) {
      if (!silent) {
        this.$store.commit('showLoading');
      }
      return this.$store.dispatch('updateReviewOptimizationOrder', {
        orderId: order_id,
        body: payload
      })
      .then(response => {
        if (response.data && response.data.status == 'SUCCESS') {
          this.loadData(silent)
        }
      })
      .finally(() => {
        if (!silent) {
          this.$store.commit('hideLoading');
        }
      })
    },
    getPDFReport(order_id, reportType, ommitInstructions = false) {

      const timestamp = moment().format("YYYY_M_D_h_mm_ss");
      this.$store.commit('showLoading');
      this.$store
        .dispatch('getReviewOptimizationOrderPDFReport', { orderId: order_id, reportType: reportType, ommitInstructions })
        .then((response) => {
          const fileName = `${reportType}_${timestamp}`
          downloadFile(response.data, fileName)
        })
        .finally(() => {
          this.$store.commit('hideLoading');
        });
    },

    // POP Report task monitors
    refreshPOPReportTaskMonitors () {
      this.clearAllPOPReportTaskIntervals()
      this.records.forEach(record => {
        if (['in_progress'].includes(record.guard_run_status) && record.guard_run_task_id) {
          console.log(record)
          this.createPOPReportTaskMonitor(record.id, record.guard_run_task_id)
        }
      })
    },
    createPOPReportTaskMonitor (recordId, taskId) {
      this.POPReportTaskIntervals = {

        ...this.POPReportTaskIntervals,

        [recordId]: {
          interval: setInterval(() => {
            this.$store.dispatch('getTaskStatus', taskId)
            .then(response => {
              if (response.data) {
                if (response.data.status == 'PENDING') {
                  // ignore
                  // Celery's PENDING state may as well mean that the task is simply unknwon
                  // (i.e. could be a really pending task, but could also be an invalid/non-existing task id or could be a completed but forgotten task)
                  this.updatePOPReportTaskMonitor(recordId, {
                    value: 5,
                    status: 'PENDING'
                  })
                } else if (response.data.status == 'PROGRESS') {
                  this.updatePOPReportTaskMonitor(recordId, {
                    value: response.data.value,
                    status: 'PROGRESS'
                  })
                } else if (response.data.status == 'SUCCESS') {
                  this.clearPOPReportTaskInterval(recordId);
                  this.loadData(true)
                } else if (response.data.status == 'FAILURE') {
                  this.clearPOPReportTaskInterval(recordId);
                }
              }
            })
          }, 3000),
          status: {
            value: 5,
            status: 'PENDING'
          }
        }
      }
    },
    updatePOPReportTaskMonitor (recordId, status) {
      this.POPReportTaskIntervals = {

        ...this.POPReportTaskIntervals,

        [recordId]: {
          interval: this.POPReportTaskIntervals?.[recordId]?.interval,
          status
        }
      }
    },
    clearPOPReportTaskInterval (recordId) {
      if (this.POPReportTaskIntervals[recordId]) {
        if (this.POPReportTaskIntervals[recordId]?.interval) {
          clearInterval(this.POPReportTaskIntervals[recordId].interval)
        }
        this.POPReportTaskIntervals = Object.fromEntries(
          Object.entries(this.POPReportTaskIntervals).filter(([key]) => key !== recordId)
        )
      }
    },
    clearAllPOPReportTaskIntervals () {
      Object.entries(this.POPReportTaskIntervals).forEach(([id]) => {
        this.clearPOPReportTaskInterval(id)
      })
    },
    exportMonthlyMetrics() {
      this.$store.commit('showLoading')

      const rows = [
        ['Month,Number of Reports,Number of Users,Number of Projects,Credits Used']
      ]

      this.$store.dispatch('getReviewOptimizationOrdersMetrics')
        .then(({ data }) => {
          data.data.forEach(item => {
            rows.push([
              item.month,
              item.reports_total,
              item.users_total,
              item.sites_total,
              item.credits_used_total,
            ].join(','))
          })
        }) 
        .then(() => {
          const fileName = "Optimization_Reviews_Metrics.csv"
          this.exportToCSV(rows.join('\n'), fileName)
        })
        .finally(() => {
          this.$store.commit('hideLoading')
        })
    },
  },
  computed: {
    currentUser() {
      if (this.$store.state.user) {
        return this.$store.state.user
      }
      return {}
    },
  },
  mounted () {
    this.loadData() 
  },
  destroyed () {
    this.clearAllPOPReportTaskIntervals()
  }
}
</script>

<style lang="scss">

</style>